import { useEffect, useState } from "react";
import axios from "axios";
import DynamicTable from "../../../Components/DynamicTable";
// import UserContext from "../../../UserContext";
import AddAccount from "./AddAccount";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import SlidingSidebar, {
  ButtonControls,
  SidebarAvatar,
  SidebarEditForm,
} from "../../../Components/SlidingSidebar";
import { useUser } from "../../../UserContext";
import Avatar from "../../../Components/Avatar";

interface Account {
  id: number;
  account: string;
  address: string;
  city: string;
  county_id: number | null;
  state_code: string;
  zip: string;
  avatar_url: string;
  owner_id?: number;
  created_at?: string;
  updated_at?: string;
}

interface State {
  id: number;
  state: string;
  state_code: string;
}

interface City {
  city_id: number;
  state_code: string;
  state_name: string;
  city: string;
  county: string;
  county_id: number;
}

const Accounts = () => {
  const { selectedOwnerId } = useUser();
  const [accountState, setAccountState] = useState({
    data: [] as Account[],
    editingId: null as number | null,
    editData: null as Account | null,
    deleteConfirmOpen: false,
    accountToDelete: null as number | null,
    sidebarOpen: false,
    snackbarOpen: false,
    snackbarMessage: "",
  });

  const [locationData, setLocationData] = useState({
    states: [] as State[],
    cities: [] as City[],
  });

  const fetchCitiesForState = async (stateCode: string) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/accounts/cities/${stateCode}`
      );
      setLocationData((prev) => ({ ...prev, cities: response.data }));
    } catch (error) {
      console.error("Failed to fetch cities:", error);
    }
  };

  const handleEditChange = (field: string, value: any) => {
    if (accountState.editData) {
      if (field === "state_code") {
        setAccountState((prev) => ({
          ...prev,
          editData: {
            ...prev.editData!,
            state_code: value,
            city: "",
            county_id: null,
          },
        }));
        fetchCitiesForState(value);
      } else if (field === "city") {
        const selectedCity = locationData.cities.find(
          (city) => city.city === value
        );
        setAccountState((prev) => ({
          ...prev,
          editData: {
            ...prev.editData!,
            city: value,
            county_id: selectedCity?.county_id || null,
          },
        }));
      } else {
        setAccountState((prev) => ({
          ...prev,
          editData: { ...prev.editData!, [field]: value },
        }));
      }
    }
  };

  // API Calls
  const fetchAccounts = async () => {
    try {
      if (!selectedOwnerId) {
        return;
      }

      const response = await axios.get<Account[]>(
        `${import.meta.env.VITE_API_URL}/accounts/accounts`,
        {
          params: { owner_id: selectedOwnerId },
        }
      );
      setAccountState((prev) => ({ ...prev, data: response.data }));
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  // Handlers
  const handleEdit = async (record: Account) => {
    setAccountState((prev) => ({
      ...prev,
      editingId: record.id,
      editData: { ...record },
    }));
    setAccountState((prev) => ({ ...prev, sidebarOpen: true }));
  };

  const handleCloseSidebar = () => {
    setAccountState((prev) => ({
      ...prev,
      sidebarOpen: false,
      editingId: null,
      editData: null,
    }));
  };

  const handleSave = async () => {
    if (!accountState.editData) return;

    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/accounts/account-profile/${
          accountState.editData.id
        }`,
        {
          account: accountState.editData.account,
          address: accountState.editData.address,
          city: accountState.editData.city,
          state_code: accountState.editData.state_code,
          zip: accountState.editData.zip,
          avatar_url: accountState.editData.avatar_url,
          county_id: accountState.editData.county_id,
        }
      );

      setAccountState((prev) => ({
        ...prev,
        data: prev.data.map((account) =>
          account.id === accountState.editingId ? response.data : account
        ),
        snackbarMessage: "Changes saved successfully",
        snackbarOpen: true,
      }));
      handleCloseSidebar();
    } catch (error) {
      console.error("Error updating account:", error);
    }
  };

  const handleDelete = (id: number) => {
    setAccountState((prev) => ({
      ...prev,
      accountToDelete: id,
      deleteConfirmOpen: true,
    }));
  };

  const handleConfirmDelete = async () => {
    if (!accountState.accountToDelete) return;

    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/accounts/account/${
          accountState.accountToDelete
        }`
      );
      setAccountState((prev) => ({
        ...prev,
        data: prev.data.filter(
          (account) => account.id !== accountState.accountToDelete
        ),
        snackbarMessage: "Account deleted successfully",
        snackbarOpen: true,
      }));
      handleCloseSidebar();
    } catch (error) {
      console.error("Error deleting account:", error);
    }

    setAccountState((prev) => ({
      ...prev,
      deleteConfirmOpen: false,
      accountToDelete: null,
    }));
  };

  const handleCloseSnackbar = () => {
    setAccountState((prev) => ({ ...prev, snackbarOpen: false }));
  };

  const refreshAccountsList = () => {
    fetchAccounts();
  };

  // Effects
  useEffect(() => {
    if (selectedOwnerId) {
      fetchAccounts();
    }
  }, [selectedOwnerId]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/util/states`
        );
        // Extract the data array from the response
        const statesData = response.data[0]?.data || [];
        setLocationData((prev) => ({ ...prev, states: statesData }));
      } catch (error) {
        console.error("Failed to fetch states:", error);
      }
    };
    fetchStates();
    console.log("States data:", locationData.states);
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      if (accountState.editData?.state_code) {
        try {
          const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/util/cities/${
              accountState.editData.state_code
            }`
          );
          setLocationData((prev) => ({ ...prev, cities: response.data }));
        } catch (error) {
          console.error("Failed to fetch cities:", error);
        }
      } else {
        setLocationData((prev) => ({ ...prev, cities: [] }));
      }
    };
    fetchCities();
  }, [accountState.editData?.state_code]);

  const handleAvatarUpload = async (file: File, accountId: number) => {
    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await axios.put(
        `${
          import.meta.env.VITE_API_URL
        }/accounts/account/upload-avatar/${accountId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Get new avatar URL from response
      const newAvatarUrl = response.data.avatar_url;

      // Update both the accounts list and editing account with new avatar URL
      setAccountState((prev) => ({
        ...prev,
        data: prev.data.map((account) =>
          account.id === accountId
            ? { ...account, avatar_url: newAvatarUrl }
            : account
        ),
        editData: prev.editData && {
          ...prev.editData,
          avatar_url: newAvatarUrl,
        },
      }));
    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  const columns = [
    {
      header: "Avatar",
      dataIndex: "avatar_url",
      key: "avatar",
      render: (_url: string, record: Account) => (
        <Avatar
          src={(() => {
            const url = record.avatar_url || "/default-avatar.png";
            console.log("Avatar URL in render:", url);
            return url;
          })()}
          alt={record.account}
          size={40}
        />
      ),
    },
    {
      header: "Account Name",
      dataIndex: "account",
      key: "account",
    },
    {
      header: "Address",
      dataIndex: "address",
      key: "address",
    },
    {
      header: "City",
      dataIndex: "city",
      key: "city",
    },
    {
      header: "State",
      dataIndex: "state_code",
      key: "state_code",
      render: (state_code: string) => state_code,
    },
    {
      header: "ZIP",
      dataIndex: "zip",
      key: "zip",
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <AddAccount onSuccess={refreshAccountsList} />
      </Box>
      <DynamicTable
        data={accountState.data}
        columns={columns}
        onRowClick={handleEdit}
      />

      <SlidingSidebar
        open={accountState.sidebarOpen}
        onClose={handleCloseSidebar}
        width="500px"
      >
        {accountState.editData && (
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
              {accountState.editData.avatar_url && (
                <SidebarAvatar
                  src={(() => {
                    const url =
                      accountState.editData.avatar_url || "/default-avatar.png";
                    console.log("Avatar URL in render:", url);
                    return url;
                  })()}
                  alt={accountState.editData.account}
                  onEditAvatar={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file && accountState.editData) {
                        handleAvatarUpload(file, accountState.editData.id);
                      }
                    };
                    input.click();
                  }}
                />
              )}

              <SidebarEditForm
                sections={[
                  {
                    title: "Account Information",
                    fields: [
                      {
                        label: "Account Name",
                        value: accountState.editData.account,
                        field: "account",
                        required: true,
                      },
                      {
                        label: "Address",
                        value: accountState.editData.address,
                        field: "address",
                        required: true,
                      },
                      {
                        label: "State",
                        value: accountState.editData.state_code,
                        field: "state_code",
                        type: "autocomplete",
                        options: locationData.states.map((state) => ({
                          label: `${state.state_code}`,
                          value: state.state_code,
                        })),
                        required: true,
                      },
                      {
                        label: "City",
                        value: accountState.editData.city,
                        field: "city",
                        type: "autocomplete",
                        options: locationData.cities.map((city) => ({
                          label: city.city,
                          value: city.city,
                        })),
                        disabled: !accountState.editData.state_code,
                        required: true,
                      },
                      {
                        label: "ZIP",
                        value: accountState.editData.zip,
                        field: "zip",
                        required: true,
                      },
                    ],
                  },
                ]}
                onValueChange={handleEditChange}
              />
            </Box>
            <ButtonControls>
              <Button onClick={handleSave} variant="contained" color="primary">
                Save Changes
              </Button>
              <Button
                onClick={() =>
                  accountState.editData &&
                  handleDelete(accountState.editData.id)
                }
                variant="outlined"
                color="error"
              >
                Delete Account
              </Button>
              <Button onClick={handleCloseSidebar} variant="outlined">
                Cancel
              </Button>
            </ButtonControls>
          </Box>
        )}
      </SlidingSidebar>

      <Dialog
        open={accountState.deleteConfirmOpen}
        onClose={() =>
          setAccountState((prev) => ({ ...prev, deleteConfirmOpen: false }))
        }
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this account? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setAccountState((prev) => ({ ...prev, deleteConfirmOpen: false }))
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={accountState.snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {accountState.snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Accounts;
